import React, { useState, useEffect } from 'react'
import { Divider, Row, Col, Form, Input, InputNumber, Radio, Tooltip, Typography, DatePicker, Select } from 'antd'
import { InfoCircleOutlined } from '@ant-design/icons'
import { VALIDATION_RULES } from '../../utils/constants'

const { Text } = Typography

const StockInformationSection = ({
  form,
  inventoryTrackingMethod,
  setInventoryTrackingMethod,
  isTrackedByPacks,
  setIsTrackedByPacks,
  calculateAvailableUnits,
  styles
}) => {
  // Handle inventory tracking method change
  const handleInventoryTrackingMethodChange = (e) => {
    setInventoryTrackingMethod(e.target.value)
    form.setFieldsValue({ inventoryTrackingMethod: e.target.value })
    // Update available units after changing tracking method
    setTimeout(updateAvailableUnits, 0)
  }

  // Local state to track available units
  const [availableUnits, setAvailableUnits] = useState(0)

  // Update available units when component mounts or tracking method changes
  useEffect(() => {
    updateAvailableUnits()

    // Debug: Log the current unitCost value when the component mounts
  }, [inventoryTrackingMethod]) // eslint-disable-line react-hooks/exhaustive-deps

  // Helper function to update reorder quantity based on selected method
  const updateReorderQuantity = (reorderType) => {
    if (reorderType === 'maximum') {
      const maxLevel = form.getFieldValue('maxStockLevel') || 0
      const minLevel = form.getFieldValue('minStockLevel') || 0
      const difference = Math.max(0, maxLevel - minLevel)
      form.setFieldsValue({ reorderQuantity: difference })
    } else if (reorderType === 'eoq') {
      const maxLevel = form.getFieldValue('maxStockLevel') || 0
      const eoqValue = Math.round(maxLevel * 0.7)
      form.setFieldsValue({ reorderQuantity: eoqValue })
    } else if (reorderType === 'moq') {
      const moqValue = form.getFieldValue('supplierMOQ') || 0
      form.setFieldsValue({ reorderQuantity: moqValue })
    }
    // For 'manual', leave the current value as is
  }

  // Helper function to update available units
  const updateAvailableUnits = () => {
    const units = calculateAvailableUnits()
    setAvailableUnits(units)
  }

  return (
    <>
      <div className={styles.sectionHeader}>Stock Information</div>

      <Row gutter={24}>
        <Col span={8}>
          <Form.Item
            name='packSize'
            label='Pack Size'
            rules={[VALIDATION_RULES.numberRequired, VALIDATION_RULES.minOne]}
            tooltip='Number of individual units in each package (populated from MedPrax data)'
            className={`${styles.field} ${styles.fieldRequired}`}
          >
            <InputNumber
              style={{ width: '100%' }}
              min={1}
              onChange={(value) => {
                // If changing pack size to something > 1, suggest tracking by packs
                if (value > 1 && !isTrackedByPacks) {
                  setIsTrackedByPacks(true)
                  form.setFieldsValue({ isTrackedByPacks: true })

                  if (inventoryTrackingMethod === 'individualUnits') {
                    setInventoryTrackingMethod('partialPacks')
                    form.setFieldsValue({ inventoryTrackingMethod: 'partialPacks' })
                  }
                }
                // Update available units after changing pack size
                setTimeout(updateAvailableUnits, 0)
              }}
            />
          </Form.Item>
        </Col>
        <Col span={16}>
          <Form.Item
            name='inventoryTrackingMethod'
            label={
              <span>
                Inventory Tracking Method
                <Tooltip title='Choose how to track inventory for this item'>
                  <InfoCircleOutlined style={{ marginLeft: 8 }} />
                </Tooltip>
              </span>
            }
            className={styles.field}
          >
            <Radio.Group
              onChange={handleInventoryTrackingMethodChange}
              value={inventoryTrackingMethod}
              buttonStyle='solid'
            >
              <Radio.Button value='wholePacksOnly'>Whole Packs Only</Radio.Button>
              <Radio.Button value='partialPacks'>Partial Packs</Radio.Button>
              <Radio.Button value='individualUnits'>Individual Units</Radio.Button>
            </Radio.Group>
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={24}>
        <Col span={6}>
          <Form.Item
            name='quantityOnHand'
            label={inventoryTrackingMethod === 'individualUnits' ? 'Individual Units on Hand' : 'Packs on Hand'}
            rules={[VALIDATION_RULES.numberRequired, VALIDATION_RULES.minZero]}
          >
            <InputNumber
              style={{ width: '100%' }}
              min={0}
              onChange={() => {
                // Update available units after changing quantity
                setTimeout(updateAvailableUnits, 0)
              }}
            />
          </Form.Item>
        </Col>

        {inventoryTrackingMethod === 'partialPacks' && (
          <Col span={6}>
            <Form.Item
              name='partialUnitsUsed'
              label='Units Used from Current Pack'
              rules={[VALIDATION_RULES.numberRequired, VALIDATION_RULES.minZero]}
              tooltip='Number of individual units that have been used from the opened pack'
            >
              <InputNumber
                style={{ width: '100%' }}
                min={0}
                max={(form.getFieldValue('packSize') || 1) - 1}
                onChange={() => {
                  // Update available units after changing partial units
                  setTimeout(updateAvailableUnits, 0)
                }}
              />
            </Form.Item>
          </Col>
        )}

        <Col span={inventoryTrackingMethod === 'partialPacks' ? 6 : 9}>
          <Form.Item label='Total Available Units' className={styles.field}>
            <div className={styles.availableUnits}>
              {(() => {
                // Manual calculation for display purposes
                const packSize = form.getFieldValue('packSize') || 1
                const qtyOnHand = form.getFieldValue('quantityOnHand') || 0
                const partialUsed = form.getFieldValue('partialUnitsUsed') || 0

                let manualCalc = 0
                if (inventoryTrackingMethod === 'wholePacksOnly') {
                  manualCalc = qtyOnHand * packSize
                } else if (inventoryTrackingMethod === 'partialPacks') {
                  manualCalc = qtyOnHand * packSize - partialUsed
                } else {
                  manualCalc = qtyOnHand
                }

                return `${manualCalc} units`
              })()}
            </div>
          </Form.Item>
        </Col>

        <Col span={inventoryTrackingMethod === 'partialPacks' ? 6 : 9}>
          <Form.Item
            name='unitCost'
            label={`Cost (ZAR per ${inventoryTrackingMethod === 'individualUnits' ? 'Unit' : 'Pack'})`}
            rules={[VALIDATION_RULES.numberRequired, VALIDATION_RULES.minZero]}
          >
            <InputNumber style={{ width: '100%' }} min={0} step={0.01} precision={2} />
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={24}>
        <Col span={8}>
          <Form.Item
            name='minStockLevel'
            label={inventoryTrackingMethod === 'individualUnits' ? 'Minimum Units' : 'Minimum Packs'}
            rules={[VALIDATION_RULES.numberRequired, VALIDATION_RULES.minZero]}
          >
            <InputNumber
              style={{ width: '100%' }}
              min={0}
              onChange={(value) => {
                // Update reorder quantity if using maximum method
                const reorderType = form.getFieldValue('reorderType')
                if (reorderType === 'maximum') {
                  updateReorderQuantity(reorderType)
                }
              }}
            />
          </Form.Item>
        </Col>
        <Col span={8}>
          <Form.Item
            name='maxStockLevel'
            label={inventoryTrackingMethod === 'individualUnits' ? 'Maximum Units' : 'Maximum Packs'}
            rules={[VALIDATION_RULES.numberRequired, VALIDATION_RULES.minZero]}
          >
            <InputNumber
              style={{ width: '100%' }}
              min={0}
              onChange={(value) => {
                // Update reorder quantity based on selected method
                const reorderType = form.getFieldValue('reorderType')
                if (reorderType === 'maximum' || reorderType === 'eoq') {
                  updateReorderQuantity(reorderType)
                }
              }}
            />
          </Form.Item>
        </Col>
        <Col span={8}>
          <Form.Item
            name='reorderType'
            label={`Reorder ${inventoryTrackingMethod === 'individualUnits' ? 'Units' : 'Packs'} Method`}
            initialValue='manual'
            className={styles.field}
          >
            <Select
              onChange={(value) => {
                // Calculate the reorder quantity based on selection
                updateReorderQuantity(value)
              }}
            >
              <Select.Option value='manual'>Manual Entry</Select.Option>
              <Select.Option value='maximum'>Maximum (Max - Min)</Select.Option>
              <Select.Option value='eoq'>EOQ (70% of Max)</Select.Option>
              <Select.Option value='moq'>Supplier MOQ</Select.Option>
            </Select>
          </Form.Item>
        </Col>
        <Col span={8}>
          <Form.Item
            name='reorderQuantity'
            label={inventoryTrackingMethod === 'individualUnits' ? 'Reorder Units' : 'Reorder Packs'}
            rules={[VALIDATION_RULES.numberRequired, VALIDATION_RULES.minZero]}
          >
            <InputNumber style={{ width: '100%' }} min={0} />
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={24}>
        <Col span={12}>
          <Form.Item name='expiryDate' label='Expiry Date'>
            <DatePicker style={{ width: '100%' }} format='DD/MM/YYYY' />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item name='costCentre' label='Cost Centre'>
            <Input placeholder='Enter cost centre' />
          </Form.Item>
        </Col>
      </Row>

      {/* Hidden field to store the legacy isTrackedByPacks value for backward compatibility */}
      <Form.Item name='isTrackedByPacks' hidden>
        <Input />
      </Form.Item>
    </>
  )
}

export default StockInformationSection
